import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { useAuthStore } from "../../stores/authStore";
import styles from "./AwsOrderFormPage.module.css";
import AwsOrderPrintView from "./AwsOrderPrintView";

const apiURL = () => process.env.REACT_APP_API_URL || "";

const SIZES = [33, 34, 35, 36, 37, 38, 39, 40, 41, 42, 43, 44, 45, 46, 47, 48];

const formatCurrency = (v: any) => {
  const n = Number(v);
  return isNaN(n)
    ? "R$ 0,00"
    : n.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
};

interface Grade {
  tamanho: number;
  quantidade: number;
}
interface Produto {
  ID: number;
  REFERENCIA: string;
  DESCRICAO: string;
  CA?: string;
  LINHA?: string;
  preco_item: number;
  tabela_preco: number;
  opcional?: string | null;
  margem: number;
  grades: Grade[];
}
interface Cliente {
  ID: number;
  NOME: string;
  CIDADE: string;
  ESTADO: string;
  SEGMENTO_NOME?: string;
}

// ─── Modal de Seleção de Cliente ─────────────────────────────────────────────
const ClienteSearchModal: React.FC<{
  onSelect: (c: Cliente) => void;
  onClose: () => void;
  token: string;
}> = ({ onSelect, onClose, token }) => {
  const [q, setQ] = useState("");
  const [resultado, setResultado] = useState<Cliente[]>([]);
  const [loading, setLoading] = useState(false);

  const buscar = async (searchQuery: string) => {
    if (searchQuery.length < 2) {
      setResultado([]);
      return;
    }
    setLoading(true);
    try {
      const res = await axios.get(`${apiURL()}/api/clients`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { search: searchQuery, page: 1, limit: 20 },
      });
      setResultado(
        (res.data.clients || []).map((c: any) => ({
          ID: c.ID,
          NOME: c.NOME,
          CIDADE: c.CIDADE,
          ESTADO: c.ESTADO,
          SEGMENTO_NOME: c.SEGMENTO_NOME || "",
        })),
      );
    } catch (e) {
      console.error("Erro na busca de clientes", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      buscar(q);
    }, 500);
    return () => clearTimeout(timer);
  }, [q]);

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modalContent}>
        <h3 style={{ marginTop: 0 }}>Selecionar Cliente</h3>
        <div style={{ display: "flex", gap: "8px", marginBottom: "16px" }}>
          <input
            className={styles.input}
            placeholder="Digite o nome do cliente..."
            value={q}
            onChange={(e) => setQ(e.target.value)}
            autoFocus
          />
          <button onClick={onClose} className={styles.btnSecondary}>
            Fechar
          </button>
        </div>
        {loading && (
          <p style={{ textAlign: "center", color: "#888" }}>Buscando...</p>
        )}
        {!loading && resultado.length === 0 && q.length >= 2 && (
          <p style={{ textAlign: "center", color: "#888" }}>
            Nenhum cliente encontrado.
          </p>
        )}
        {resultado.map((c) => (
          <div
            key={c.ID}
            onClick={() => onSelect(c)}
            style={{
              padding: "10px",
              borderBottom: "1px solid #eee",
              cursor: "pointer",
              borderRadius: "4px",
              transition: "background 0.15s",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = "#f5f5f5")}
            onMouseLeave={(e) =>
              (e.currentTarget.style.background = "transparent")
            }
          >
            <strong>{c.NOME}</strong>{" "}
            <span style={{ color: "#888", fontSize: "0.9em" }}>
              ({c.CIDADE}/{c.ESTADO})
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

// ─── Modal de Seleção de Produto ──────────────────────────────────────────────
const ProdutoSearchModal: React.FC<{
  onSelect: (p: any) => void;
  onClose: () => void;
  token: string;
}> = ({ onSelect, onClose, token }) => {
  const [q, setQ] = useState("");
  const [resultado, setResultado] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  const buscar = async (searchQuery: string) => {
    if (searchQuery.length < 2) {
      setResultado([]);
      return;
    }
    setLoading(true);
    try {
      const res = await axios.get(`${apiURL()}/api/products/search`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { q: searchQuery, page: 1, limit: 30 },
      });
      setResultado(
        Array.isArray(res.data) ? res.data : res.data.products || [],
      );
    } catch (e) {
      console.error("Erro na busca de produtos", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      buscar(q);
    }, 500);
    return () => clearTimeout(timer);
  }, [q]);

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modalContent} style={{ maxWidth: "700px" }}>
        <h3 style={{ marginTop: 0 }}>Selecionar Produto</h3>
        <div style={{ display: "flex", gap: "8px", marginBottom: "16px" }}>
          <input
            className={styles.input}
            placeholder="Referência ou descrição..."
            value={q}
            onChange={(e) => setQ(e.target.value)}
            autoFocus
          />
          <button onClick={onClose} className={styles.btnSecondary}>
            Fechar
          </button>
        </div>
        {loading && (
          <p style={{ textAlign: "center", color: "#888" }}>Buscando...</p>
        )}
        {!loading && resultado.length === 0 && q.length >= 2 && (
          <p style={{ textAlign: "center", color: "#888" }}>
            Nenhum produto encontrado.
          </p>
        )}
        {resultado.map((p: any) => (
          <div
            key={p.ID}
            onClick={() => onSelect(p)}
            style={{
              padding: "10px",
              borderBottom: "1px solid #eee",
              cursor: "pointer",
              borderRadius: "4px",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = "#f5f5f5")}
            onMouseLeave={(e) =>
              (e.currentTarget.style.background = "transparent")
            }
          >
            <strong>{p.REFERENCIA}</strong> — {p.DESCRICAO}
          </div>
        ))}
      </div>
    </div>
  );
};

// ─── Modal de Grade + Preço ────────────────────────────────────────────────────
const GradeModal: React.FC<{
  produto: any;
  opcionais: any[];
  onConfirm: (p: Produto) => void;
  onClose: () => void;
  clienteId: number;
  segmento: string;
  condicaoPgto: string;
  operacaoFiscal: string;
  token: string;
}> = ({
  produto,
  opcionais,
  onConfirm,
  onClose,
  clienteId,
  segmento,
  condicaoPgto,
  operacaoFiscal,
  token,
}) => {
  const [tabela, setTabela] = useState<number>(produto.tabela_preco || 3);
  const [preco, setPreco] = useState<number>(produto.preco_item || 0);
  const [opcional, setOpcional] = useState<string>(produto.opcional || "");
  const [loadingPrice, setLoadingPrice] = useState(false);
  const [productImg, setProductImg] = useState<string | null>(null);
  const [faixaImbiseg, setFaixaImbiseg] = useState<number>(0); // comissão IMBISEG selecionada
  const [pricelist, setPricelist] = useState<any[]>([]);
  const [tierAuto, setTierAuto] = useState<any>(null); // tier auto-detectado pela qtd
  const isImbiseg =
    (produto.GRUPO2 || "").toUpperCase() === "IMBISEG" ||
    (produto.LINHA2 || "").toUpperCase().includes("IMBISEG");
  // Comissões únicas (sem repetir)
  const faixasUnicas = Array.from(
    new Set(pricelist.map((p: any) => parseFloat(p.comissao) * 100)),
  ).sort((a: number, b: number) => a - b);
  const [grades, setGrades] = useState<Grade[]>(
    SIZES.map((s) => {
      // O backend devolve `tamanho` como string (coluna VARCHAR do MySQL).
      // Sem normalizar para número a comparação estrita falha e a grade já
      // cadastrada abre toda zerada.
      const existing = produto.grades?.find((g: any) => Number(g.tamanho) === s);
      return {
        tamanho: s,
        quantidade: existing ? Number(existing.quantidade) || 0 : 0,
      };
    }),
  );

  // Busca a imagem do produto
  useEffect(() => {
    const fetchImg = async () => {
      try {
        const ref = encodeURIComponent(produto.REFERENCIA);
        const res = await axios.get(
          `${apiURL()}/api/products/aws-details/${ref}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );
        setProductImg(res.data.img || null);
      } catch (e) {
        console.error("Erro ao carregar imagem do produto", e);
      }
    };
    fetchImg();
  }, [produto.REFERENCIA, token]);

  // Busca pricelist IMBISEG se o produto for IMBISEG
  useEffect(() => {
    if (!isImbiseg) return;
    const fetchPricelist = async () => {
      try {
        const ref = encodeURIComponent(produto.REFERENCIA);
        const res = await axios.get(
          `${apiURL()}/api/products/imbiseg-prices/${ref}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );
        const list = Array.isArray(res.data) ? res.data : [];
        setPricelist(list);
        // Seleciona a primeira faixa como padrão
        if (list.length > 0 && faixaImbiseg === 0) {
          setFaixaImbiseg(parseFloat(list[0].comissao) * 100);
        }
      } catch (e) {
        console.error("Erro ao buscar pricelist IMBISEG", e);
      }
    };
    fetchPricelist();
  }, [produto.REFERENCIA, isImbiseg, token]);

  useEffect(() => {
    const fetchPrice = async () => {
      if (!clienteId || !segmento || !condicaoPgto) return;
      try {
        setLoadingPrice(true);
        const res = await axios.post(
          `${apiURL()}/api/orders/aws/calculate-price`,
          {
            referencia: produto.REFERENCIA,
            clienteId,
            segmento,
            tabela_preco: tabela,
            condicaoPagamento: condicaoPgto,
            opcional,
            operacaoFiscal,
            qtd: totalQtd,
            com_imb: isImbiseg ? faixaImbiseg : 0,
            tipoFrete: null, // Será buscado do cliente no backend
          },
          { headers: { Authorization: `Bearer ${token}` } },
        );
        setPreco(res.data.preco);
      } catch (e) {
        console.error("Erro ao calcular preço", e);
      } finally {
        setLoadingPrice(false);
      }
    };
    fetchPrice();
  }, [
    tabela,
    opcional,
    grades,
    produto.REFERENCIA,
    faixaImbiseg,
    isImbiseg,
    clienteId,
    segmento,
    condicaoPgto,
    operacaoFiscal,
    token,
  ]);

  // Auto-detectar tier IMBISEG conforme a quantidade total muda
  const totalQtd = grades.reduce((s, g) => s + g.quantidade, 0);
  useEffect(() => {
    if (!isImbiseg || pricelist.length === 0 || totalQtd === 0) {
      setTierAuto(null);
      return;
    }
    const targetFaixa = faixaImbiseg / 100;
    const entries = pricelist.filter(
      (p: any) => Math.abs(parseFloat(p.comissao) - targetFaixa) < 0.0001,
    );
    const tier =
      entries.find(
        (p: any) => totalQtd >= p.qtde_minima && totalQtd <= p.qtde_maxima,
      ) ||
      entries[entries.length - 1] ||
      null;
    setTierAuto(tier);
  }, [totalQtd, faixaImbiseg, isImbiseg, pricelist]);

  const handleGradeChange = (tamanho: number, quantidade: number) => {
    setGrades((prev) =>
      prev.map((g) => (g.tamanho === tamanho ? { ...g, quantidade } : g)),
    );
  };

  const totalValor = totalQtd * preco;

  const handleConfirm = () => {
    if (!preco || preco <= 0) {
      alert("Informe o preço!");
      return;
    }
    if (totalQtd === 0) {
      alert("Adicione ao menos uma unidade na grade!");
      return;
    }
    const gradesAtivas = grades.filter((g) => g.quantidade > 0);
    onConfirm({
      ID: produto.ID,
      REFERENCIA: produto.REFERENCIA,
      DESCRICAO: produto.DESCRICAO,
      CA: produto.CA,
      LINHA: produto.LINHA,
      preco_item: preco,
      tabela_preco: tabela,
      opcional: opcional || null,
      margem: 0,
      grades: gradesAtivas,
    });
  };

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modalContent} style={{ maxWidth: "700px" }}>
        <div style={{ display: "flex", gap: "20px", marginBottom: "16px" }}>
          {productImg ? (
            <img
              src={productImg}
              alt={produto.DESCRICAO}
              style={{
                width: "120px",
                height: "120px",
                objectFit: "contain",
                border: "1px solid #e0e0e0",
                borderRadius: "6px",
                background: "#fafafa",
                flexShrink: 0,
              }}
            />
          ) : (
            <div
              style={{
                width: "120px",
                height: "120px",
                background: "#f5f5f5",
                border: "1px solid #e0e0e0",
                borderRadius: "6px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#bbb",
                fontSize: "0.8rem",
                flexShrink: 0,
              }}
            >
              Sem foto
            </div>
          )}
          <div>
            <h3 style={{ marginTop: 0 }}>
              {produto.REFERENCIA} — {produto.DESCRICAO}
            </h3>
          </div>
        </div>

        <div className={styles.grid3}>
          <div>
            <label className={styles.label}>TABELA</label>
            <select
              className={styles.select}
              value={tabela}
              onChange={(e) => setTabela(Number(e.target.value))}
            >
              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((t) => (
                <option key={t} value={t}>
                  R{t}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className={styles.label}>
              PREÇO (R$) {loadingPrice && "..."}
            </label>
            <input
              className={styles.input}
              type="number"
              step="0.01"
              value={preco}
              onChange={(e) => setPreco(parseFloat(e.target.value) || 0)}
              style={{ background: loadingPrice ? "#f0f0f0" : "#fff" }}
            />
          </div>
          <div>
            <label className={styles.label}>OPCIONAL</label>
            <select
              className={styles.select}
              value={opcional}
              onChange={(e) => setOpcional(e.target.value)}
            >
              <option value="">Sem Opcional</option>
              {opcionais.map((op: any) => (
                <option key={op.ID} value={op.ID}>
                  {op.OPCIONAL}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Seletor de Faixa IMBISEG */}
        {isImbiseg && faixasUnicas.length > 0 && (
          <div style={{ marginTop: "16px" }}>
            <label className={styles.label}>🔶 FAIXA IMBISEG</label>
            <select
              className={styles.select}
              value={faixaImbiseg}
              onChange={(e) => setFaixaImbiseg(Number(e.target.value))}
            >
              {faixasUnicas.map((comPct: number) => (
                <option key={comPct} value={comPct}>
                  {comPct}%
                </option>
              ))}
            </select>
            {tierAuto && (
              <div
                style={{
                  marginTop: 6,
                  fontSize: "0.85em",
                  color: "#555",
                  background: "#fef3c7",
                  padding: "6px 10px",
                  borderRadius: 4,
                }}
              >
                Qtd {tierAuto.qtde_minima}–{tierAuto.qtde_maxima} pares → Preço
                base: R$ {Number(tierAuto.preço).toFixed(2)}
              </div>
            )}
            {!tierAuto && totalQtd > 0 && (
              <div style={{ marginTop: 6, fontSize: "0.85em", color: "#c00" }}>
                Quantidade ({totalQtd}) fora das faixas disponíveis
              </div>
            )}
          </div>
        )}

        <label className={styles.label} style={{ marginTop: "16px" }}>
          GRADE DE TAMANHOS
        </label>
        <div className={styles.gradeGrid}>
          {SIZES.map((s) => (
            <div key={s} className={styles.gradeBox}>
              <div className={styles.sizeLabel}>{s}</div>
              <input
                className={styles.qtyInput}
                type="number"
                min={0}
                value={grades.find((g) => g.tamanho === s)?.quantidade || 0}
                onChange={(e) =>
                  handleGradeChange(s, parseInt(e.target.value) || 0)
                }
              />
            </div>
          ))}
        </div>

        <div className={styles.totalRow}>
          <span>
            <strong>Qtd Total:</strong> {totalQtd}
          </span>
          <span>
            <strong>Valor Total:</strong> {formatCurrency(totalValor)}
          </span>
        </div>

        <div className={styles.footerActions}>
          <button onClick={onClose} className={styles.btnSecondary}>
            Cancelar
          </button>
          <button onClick={handleConfirm} className={styles.btnPrimary}>
            Confirmar
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── PÁGINA PRINCIPAL ─────────────────────────────────────────────────────────
const AwsOrderFormPage: React.FC = () => {
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const { token } = useAuthStore();
  const isEditMode = !!id;

  const [createdOrderId, setCreatedOrderId] = useState<number | null>(null);
  const [loading, setLoading] = useState(isEditMode);
  const [saving, setSaving] = useState(false);
  const [erro, setErro] = useState("");

  // Pedido
  const [cliente, setCliente] = useState<Cliente | null>(null);
  const [segmento, setSegmento] = useState("");
  const [operacaoFiscal, setOperacaoFiscal] = useState("COTAÇÃO");
  const [condicaoPgto, setCondicaoPgto] = useState("");
  const [obs, setObs] = useState("");
  const [produtos, setProdutos] = useState<Produto[]>([]);

  // Amostra
  const [possuiFilial, setPossuiFilial] = useState("");
  const [unidadeCompra, setUnidadeCompra] = useState("");
  const [numUsuarios, setNumUsuarios] = useState("");
  const [contatoPrincipal, setContatoPrincipal] = useState("");
  const [telefoneCont, setTelefoneCont] = useState("");
  const [emailCont, setEmailCont] = useState("");
  const [caMarcaAtual, setCaMarcaAtual] = useState("");
  const [riscosArea, setRiscosArea] = useState("");
  const [insatisfacaoAtual, setInsatisfacaoAtual] = useState("");
  const [calcadoSugerido, setCalcadoSugerido] = useState("");
  const [observacoesAmostra, setObservacoesAmostra] = useState("");

  const isAmostra = operacaoFiscal.includes("AMOSTRA");

  // DEBUG: Log da detecção de amostra
  useEffect(() => {
    console.log("=== DEBUG OPERACAO FISCAL ===");
    console.log("Valor:", operacaoFiscal);
    console.log("isAmostra:", isAmostra);
    console.log("============================");
  }, [operacaoFiscal, isAmostra]);

  // Auxiliares
  const [condicoesList, setCondicoesList] = useState<string[]>([]);
  const [opcionaisList, setOpcionaisList] = useState<any[]>([]);
  const [operacoesList, setOperacoesList] = useState<any[]>([]);
  const [showClienteModal, setShowClienteModal] = useState(false);
  const [showProdutoModal, setShowProdutoModal] = useState(false);
  const [produtoParaGrade, setProdutoParaGrade] = useState<any | null>(null);

  useEffect(() => {
    const loadOptions = async () => {
      try {
        const [condRes, optsRes, opsRes] = await Promise.all([
          axios.get(`${apiURL()}/api/orders/payment-conditions`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          axios.get(`${apiURL()}/api/orders/options`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          axios.get(`${apiURL()}/api/orders/fiscal-operations`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
        ]);
        const conds = condRes.data;
        setCondicoesList(
          Array.isArray(conds) ? conds.map((c: any) => c.DESCRICAO || c) : [],
        );
        setOpcionaisList(Array.isArray(optsRes.data) ? optsRes.data : []);
        // Apenas Cotação e Amostra Homologação para pedidos AWS
        setOperacoesList(
          Array.isArray(opsRes.data)
            ? opsRes.data.filter(
                (op: any) =>
                  op.DESCRICAO === "COTAÇÃO" ||
                  op.DESCRICAO.toUpperCase().includes("HOMOLOGA"),
              )
            : [],
        );
      } catch (e) {
        console.error("Erro ao carregar opções", e);
      }
    };
    loadOptions();
  }, [token]);

  useEffect(() => {
    if (!isEditMode) return;
    const loadPedido = async () => {
      try {
        setLoading(true);
        const res = await axios.get(`${apiURL()}/api/orders/aws/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const { pedido, produtos: prods, amostra } = res.data;
        setCliente({
          ID: pedido.cliente_id,
          NOME: pedido.cliente_nome,
          CIDADE: pedido.cidade,
          ESTADO: pedido.estado,
          SEGMENTO_NOME: pedido.segmento_nome,
        });
        setSegmento(pedido.segmento_nome || "");
        setOperacaoFiscal(pedido.operacao_fiscal || "COTAÇÃO");
        setCondicaoPgto(pedido.condicao_pgto || "");
        setObs(pedido.obs || "");
        setProdutos(
          prods.map((p: any) => ({
            ID: p.id_produto,
            REFERENCIA: p.REFERENCIA,
            DESCRICAO: p.DESCRICAO,
            CA: p.CA,
            LINHA: p.LINHA,
            preco_item: p.preco_item,
            tabela_preco: p.tabela_preco,
            opcional: p.opcional,
            margem: p.margem || 0,
            // `tamanho` vem como string do MySQL — normaliza para número para
            // a grade não perder os valores já cadastrados ao editar.
            grades: (p.grades || []).map((g: any) => ({
              tamanho: Number(g.tamanho),
              quantidade: Number(g.quantidade) || 0,
            })),
          })),
        );

        // Carrega os dados de amostra salvos (obrigatórios para pedidos de AMOSTRA)
        if (amostra) {
          setPossuiFilial(amostra.possui_filial || "");
          setUnidadeCompra(amostra.unidade_compra || "");
          setNumUsuarios(
            amostra.num_usuarios !== null && amostra.num_usuarios !== undefined
              ? String(amostra.num_usuarios)
              : "",
          );
          setContatoPrincipal(amostra.contato_principal || "");
          setTelefoneCont(amostra.telefone_contato || "");
          setEmailCont(amostra.email_contato || "");
          setCaMarcaAtual(amostra.ca_marca_atual || "");
          setRiscosArea(amostra.riscos_area || "");
          setInsatisfacaoAtual(amostra.insatisfacao_atual || "");
          setCalcadoSugerido(amostra.calcado_sugerido || "");
          setObservacoesAmostra(amostra.observacoes || "");
        }
      } catch (e) {
        setErro("Erro ao carregar pedido.");
      } finally {
        setLoading(false);
      }
    };
    loadPedido();
  }, [id, isEditMode, token]);

  const handleSelecionarCliente = (c: Cliente) => {
    setCliente(c);
    if (c.SEGMENTO_NOME) setSegmento(c.SEGMENTO_NOME);
    setShowClienteModal(false);
  };

  const handleSelecionarProduto = (p: any) => {
    setProdutoParaGrade(p);
    setShowProdutoModal(false);
  };

  const handleConfirmarGrade = (p: Produto) => {
    setProdutos((prev) => {
      const idx = prev.findIndex((x) => x.ID === p.ID);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = p;
        return next;
      }
      return [...prev, p];
    });
    setProdutoParaGrade(null);
  };

  const handleRemoverProduto = (prodId: number) => {
    if (!window.confirm("Remover este produto?")) return;
    setProdutos((prev) => prev.filter((p) => p.ID !== prodId));
  };

  const valorTotal = produtos.reduce((s, p) => {
    const qtd = p.grades.reduce((g, gr) => g + gr.quantidade, 0);
    return s + qtd * p.preco_item;
  }, 0);

  const handleSalvar = async () => {
    if (!cliente) {
      setErro("Selecione um cliente!");
      return;
    }
    if (!condicaoPgto) {
      setErro("Selecione a condição de pagamento!");
      return;
    }
    if (produtos.length === 0) {
      setErro("Adicione ao menos um produto!");
      return;
    }

    // Validações de Amostra
    if (isAmostra) {
      if (!possuiFilial.trim()) {
        setErro('Preencha o campo "Possui Filial ou Matriz"!');
        return;
      }
      if (!unidadeCompra.trim()) {
        setErro('Preencha o campo "Unidade de Compra"!');
        return;
      }
      if (!numUsuarios.trim()) {
        setErro('Preencha o campo "Número de Usuários"!');
        return;
      }
      if (!contatoPrincipal.trim()) {
        setErro('Preencha o campo "Contato Principal"!');
        return;
      }
      if (!telefoneCont.trim()) {
        setErro('Preencha o campo "Telefone de Contato"!');
        return;
      }
      if (!emailCont.trim()) {
        setErro('Preencha o campo "E-mail de Contato"!');
        return;
      }
      if (!caMarcaAtual.trim()) {
        setErro('Preencha o campo "CA e Marca Atual"!');
        return;
      }
      if (!riscosArea.trim()) {
        setErro('Preencha o campo "Riscos da Área"!');
        return;
      }
      if (!insatisfacaoAtual.trim()) {
        setErro('Preencha o campo "Insatisfação Atual"!');
        return;
      }
      if (!calcadoSugerido.trim()) {
        setErro('Preencha o campo "Calçado Sugerido"!');
        return;
      }
    }

    setSaving(true);
    setErro("");
    try {
      const payload: any = {
        cliente_id: cliente.ID,
        cliente_nome: cliente.NOME,
        segmento_nome: segmento,
        operacao_fiscal: operacaoFiscal,
        condicao_pgto: condicaoPgto,
        estado: cliente.ESTADO,
        cidade: cliente.CIDADE,
        obs,
        valor_total: valorTotal,
        produtos,
      };

      if (isAmostra) {
        payload.amostra = {
          possui_filial: possuiFilial,
          unidade_compra: unidadeCompra,
          num_usuarios: numUsuarios,
          contato_principal: contatoPrincipal,
          telefone_contato: telefoneCont,
          email_contato: emailCont,
          ca_marca_atual: caMarcaAtual,
          riscos_area: riscosArea,
          insatisfacao_atual: insatisfacaoAtual,
          calcado_sugerido: calcadoSugerido,
          observacoes: observacoesAmostra,
        };
      }

      if (isEditMode) {
        await axios.put(`${apiURL()}/api/orders/aws/${id}`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        alert("Pedido atualizado com sucesso!");
        navigate("/aws-pedidos");
      } else {
        const res = await axios.post(`${apiURL()}/api/orders/aws`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const novoId = Number(res.data?.insertId);
        if (!novoId || isNaN(novoId)) {
          setErro("Pedido criado mas ID inválido. Tente novamente.");
        } else {
          setCreatedOrderId(novoId);
        }
      }
    } catch (e: any) {
      const msg =
        e?.response?.data?.error ||
        e?.response?.data?.message ||
        e?.message ||
        "Erro ao salvar pedido.";
      setErro(msg);
    } finally {
      setSaving(false);
    }
  };

  // Tela de impressão pós-criação
  if (createdOrderId) {
    return (
      <AwsOrderPrintView
        pedidoId={createdOrderId}
        autoDownload
        onClose={() => navigate("/aws-pedidos")}
      />
    );
  }

  if (loading)
    return (
      <div style={{ textAlign: "center", padding: "40px" }}>Carregando...</div>
    );

  return (
    <div className={styles.formContainer}>
      <div className={styles.topHeader}>
        <button
          onClick={() => navigate("/aws-pedidos")}
          className={styles.btnSecondary}
        >
          ← Voltar
        </button>
        {isEditMode && isAmostra && (
          <button
            onClick={() => navigate(`/amostras-pedidos/${id}/avaliar`)}
            style={{ background: "#2563eb", color: "#fff" }}
            className={styles.btnPrimary}
          >
            📋 Ficha de Avaliação
          </button>
        )}
        <h2 style={{ margin: 0, color: "#57171e" }}>
          {isEditMode ? `Editar Pedido #${id}` : "Novo Pedido"}
        </h2>
      </div>

      {erro && (
        <p
          style={{
            color: "red",
            background: "#fff3f3",
            padding: "10px 14px",
            borderRadius: "4px",
            marginBottom: "16px",
          }}
        >
          {erro}
        </p>
      )}

      {/* Cliente */}
      <div className={styles.card}>
        <div className={styles.cardHeader}>
          <h3 style={{ margin: 0, color: "#333" }}>Cliente</h3>
          {!isEditMode && (
            <button
              onClick={() => setShowClienteModal(true)}
              className={styles.btnPrimary}
            >
              {cliente ? "Trocar Cliente" : "Selecionar Cliente"}
            </button>
          )}
        </div>
        {cliente ? (
          <p style={{ margin: 0 }}>
            <strong>{cliente.NOME}</strong> — {cliente.CIDADE}/{cliente.ESTADO}
          </p>
        ) : (
          <p style={{ color: "#999", margin: 0 }}>Nenhum cliente selecionado</p>
        )}
      </div>

      {/* Formulário */}
      <div className={`${styles.card} ${styles.grid2}`}>
        <div>
          <label className={styles.label}>SEGMENTO</label>
          <input
            className={styles.input}
            value={segmento}
            onChange={(e) => setSegmento(e.target.value)}
            disabled={isEditMode}
          />
        </div>
        <div>
          <label className={styles.label}>OPERAÇÃO FISCAL</label>
          <select
            className={styles.select}
            value={operacaoFiscal}
            onChange={(e) => setOperacaoFiscal(e.target.value)}
            disabled={isEditMode}
          >
            <option value="">⌄ Selecione</option>
            {operacoesList.map((op) => (
              <option key={op.ID} value={op.DESCRICAO}>
                {op.DESCRICAO}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className={styles.label}>CONDIÇÃO DE PAGAMENTO</label>
          <select
            className={styles.select}
            value={condicaoPgto}
            onChange={(e) => setCondicaoPgto(e.target.value)}
          >
            <option value="">⌄ Selecione</option>
            {condicoesList.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </div>
        <div style={{ gridColumn: "1 / -1" }}>
          <label className={styles.label}>OBSERVAÇÕES</label>
          <textarea
            className={styles.textarea}
            value={obs}
            onChange={(e) => setObs(e.target.value)}
            rows={3}
          />
        </div>
      </div>

      {/* Seção de Amostra */}
      {isAmostra && (
        <div className={styles.card}>
          <h3 style={{ marginTop: 0, color: "#57171e" }}>
            📋 Informações de Amostra
          </h3>
          <div className={styles.grid2}>
            <div>
              <label className={styles.label}>Possui Filial ou Matriz?</label>
              <select
                className={styles.select}
                value={possuiFilial}
                onChange={(e) => setPossuiFilial(e.target.value)}
              >
                <option value="">Selecione</option>
                <option value="Sim">Sim</option>
                <option value="Não">Não</option>
              </select>
            </div>
            <div>
              <label className={styles.label}>Unidade de Compra</label>
              <input
                className={styles.input}
                value={unidadeCompra}
                onChange={(e) => setUnidadeCompra(e.target.value)}
              />
            </div>
            <div>
              <label className={styles.label}>Número de Usuários</label>
              <input
                className={styles.input}
                value={numUsuarios}
                onChange={(e) => setNumUsuarios(e.target.value)}
              />
            </div>
            <div>
              <label className={styles.label}>Contato Principal</label>
              <input
                className={styles.input}
                value={contatoPrincipal}
                onChange={(e) => setContatoPrincipal(e.target.value)}
              />
            </div>
            <div>
              <label className={styles.label}>Telefone de Contato</label>
              <input
                className={styles.input}
                value={telefoneCont}
                onChange={(e) => setTelefoneCont(e.target.value)}
              />
            </div>
            <div>
              <label className={styles.label}>E-mail de Contato</label>
              <input
                className={styles.input}
                value={emailCont}
                onChange={(e) => setEmailCont(e.target.value)}
              />
            </div>
            <div>
              <label className={styles.label}>CA e Marca Atual</label>
              <input
                className={styles.input}
                value={caMarcaAtual}
                onChange={(e) => setCaMarcaAtual(e.target.value)}
              />
            </div>
            <div>
              <label className={styles.label}>Riscos da Área</label>
              <input
                className={styles.input}
                value={riscosArea}
                onChange={(e) => setRiscosArea(e.target.value)}
              />
            </div>
            <div>
              <label className={styles.label}>Insatisfação Atual</label>
              <input
                className={styles.input}
                value={insatisfacaoAtual}
                onChange={(e) => setInsatisfacaoAtual(e.target.value)}
              />
            </div>
            <div>
              <label className={styles.label}>Calçado Sugerido</label>
              <input
                className={styles.input}
                value={calcadoSugerido}
                onChange={(e) => setCalcadoSugerido(e.target.value)}
              />
            </div>
            <div style={{ gridColumn: "1 / -1" }}>
              <label className={styles.label}>Observações da Amostra</label>
              <textarea
                className={styles.textarea}
                value={observacoesAmostra}
                onChange={(e) => setObservacoesAmostra(e.target.value)}
                rows={3}
              />
            </div>
          </div>
        </div>
      )}

      {/* Produtos */}
      <div className={styles.card}>
        <div className={styles.cardHeader}>
          <h3 style={{ margin: 0 }}>Produtos ({produtos.length})</h3>
          <button
            onClick={() => setShowProdutoModal(true)}
            className={styles.btnPrimary}
            disabled={!cliente || !operacaoFiscal || !condicaoPgto}
            title={
              !cliente
                ? "Selecione um cliente primeiro"
                : !operacaoFiscal
                  ? "Selecione a operação fiscal"
                  : !condicaoPgto
                    ? "Selecione a condição de pagamento"
                    : ""
            }
            style={
              !cliente || !operacaoFiscal || !condicaoPgto
                ? { opacity: 0.5, cursor: "not-allowed" }
                : {}
            }
          >
            + Adicionar Produto
          </button>
        </div>

        {produtos.length === 0 ? (
          <p style={{ color: "#999", textAlign: "center", padding: "20px" }}>
            Nenhum produto adicionado.
          </p>
        ) : (
          <div className={styles.tableWrapper}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Ref.</th>
                  <th>Descrição</th>
                  <th style={{ textAlign: "center" }}>Tab.</th>
                  <th style={{ textAlign: "right" }}>Preço</th>
                  {SIZES.map((s) => (
                    <th
                      key={s}
                      style={{ textAlign: "center", background: "#f1f3f5" }}
                    >
                      {s}
                    </th>
                  ))}
                  <th style={{ textAlign: "center" }}>Qtd</th>
                  <th style={{ textAlign: "right" }}>Total</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {produtos.map((p, idx) => {
                  const gradeMap: Record<number, number> = {};
                  for (const g of p.grades) gradeMap[g.tamanho] = g.quantidade;
                  const qtd = p.grades.reduce((s, g) => s + g.quantidade, 0);
                  return (
                    <tr
                      key={p.ID}
                      style={{ background: idx % 2 === 0 ? "#fff" : "#fafafa" }}
                    >
                      <td style={{ fontWeight: "bold" }}>{p.REFERENCIA}</td>
                      <td>{p.DESCRICAO}</td>
                      <td style={{ textAlign: "center" }}>R{p.tabela_preco}</td>
                      <td style={{ textAlign: "right" }}>
                        {formatCurrency(p.preco_item)}
                      </td>
                      {SIZES.map((s) => (
                        <td
                          key={s}
                          style={{
                            textAlign: "center",
                            color: gradeMap[s] ? "#000" : "#e0e0e0",
                            fontWeight: gradeMap[s] ? "bold" : "normal",
                            background: gradeMap[s] ? "#e8f5e9" : "transparent",
                          }}
                        >
                          {gradeMap[s] || "-"}
                        </td>
                      ))}
                      <td style={{ textAlign: "center", fontWeight: "bold" }}>
                        {qtd}
                      </td>
                      <td
                        style={{
                          textAlign: "right",
                          fontWeight: "bold",
                          color: "#57171e",
                        }}
                      >
                        {formatCurrency(qtd * p.preco_item)}
                      </td>
                      <td>
                        <button
                          onClick={() => {
                            setProdutoParaGrade(p);
                          }}
                          title="Editar"
                          style={{
                            background: "none",
                            border: "none",
                            cursor: "pointer",
                            marginRight: "8px",
                            fontSize: "1.1em",
                          }}
                        >
                          ✏️
                        </button>
                        <button
                          onClick={() => handleRemoverProduto(p.ID)}
                          title="Remover"
                          style={{
                            background: "none",
                            border: "none",
                            cursor: "pointer",
                            color: "#c00",
                            fontSize: "1.1em",
                          }}
                        >
                          🗑️
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <div className={styles.totalRow}>
          <span>
            Qtd Total:{" "}
            {produtos.reduce(
              (s, p) => s + p.grades.reduce((g, gr) => g + gr.quantidade, 0),
              0,
            )}
          </span>
          <span>Valor Total: {formatCurrency(valorTotal)}</span>
        </div>
      </div>

      {erro && (
        <p
          style={{
            color: "red",
            background: "#fff3f3",
            padding: "10px 14px",
            borderRadius: "4px",
            marginTop: "16px",
            marginBottom: 0,
            fontWeight: 600,
          }}
        >
          {erro}
        </p>
      )}

      <div className={styles.footerActions}>
        <button
          onClick={() => navigate("/aws-pedidos")}
          className={styles.btnSecondary}
        >
          Cancelar
        </button>
        <button
          onClick={handleSalvar}
          disabled={saving}
          className={styles.btnPrimary}
          style={{ padding: "12px 24px", opacity: saving ? 0.7 : 1 }}
        >
          {saving
            ? "Salvando..."
            : isEditMode
              ? "Salvar Alterações"
              : "Cadastrar Pedido"}
        </button>
      </div>

      {showClienteModal && (
        <ClienteSearchModal
          token={token!}
          onSelect={handleSelecionarCliente}
          onClose={() => setShowClienteModal(false)}
        />
      )}
      {showProdutoModal && (
        <ProdutoSearchModal
          token={token!}
          onSelect={handleSelecionarProduto}
          onClose={() => setShowProdutoModal(false)}
        />
      )}
      {produtoParaGrade && (
        <GradeModal
          produto={produtoParaGrade}
          opcionais={opcionaisList}
          onConfirm={handleConfirmarGrade}
          onClose={() => setProdutoParaGrade(null)}
          clienteId={cliente?.ID || 0}
          segmento={segmento}
          condicaoPgto={condicaoPgto}
          operacaoFiscal={operacaoFiscal}
          token={token!}
        />
      )}
    </div>
  );
};

export default AwsOrderFormPage;
